import { Component, ElementRef, OnDestroy, OnInit, inject, input, signal } from "@angular/core";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Subject, Subscription, from, of } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from "rxjs/operators";
import { Bind } from "primeng/bind";
import { InputText } from "primeng/inputtext";

import { TPipe } from "../../i18n/t.pipe";
interface Suggestion {
  address: string;
  score: number;
  detail: string;
  zip: string;
  city: string;
  province: string;
  region: string;
  country: string;
  lat: number;
  lng: number;
}

@Component({
    standalone: true,
    selector: "app-address-suggest",
    templateUrl: "./address-suggest.component.html",
    styles: [`
    :host { display: block; width: 100%; }
    .suggest { position: relative; width: 100%; }
    .suggest input { display: block; width: 100%; box-sizing: border-box; }
    .suggest ul { position: absolute; left: 0; right: 0; top: 100%; z-index: 1100; margin: 4px 0 0; padding: 4px 0; list-style: none;
      background: #fff; border: 1px solid #d1d5db; border-radius: 6px; box-shadow: 0 8px 24px rgba(0,0,0,.12); max-height: 280px; overflow: auto; }
    .suggest li { padding: 8px 12px; cursor: pointer; line-height: 1.3; }
    .suggest li small { display: block; color: #6b7280; }
    .suggest li.active, .suggest li.opt:hover { background: #ecfdf5; }
    .suggest li.note { cursor: default; color: #6b7280; }
    .suggest li.credit { cursor: default; color: #9ca3af; font-size: 11px; text-align: right; padding: 4px 12px 2px; }
  `],
    imports: [TPipe, FormsModule, ReactiveFormsModule, Bind, InputText]
})
export class AddressSuggestComponent implements OnInit, OnDestroy {
  private host = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly form = input.required<FormGroup>();

  readonly field = input("address");
  readonly placeholder = input("Inizia a scrivere: via e numero");

  readonly countries = input<string[]>([]);
  readonly photonUrl = input("https://photon.komoot.io");

  readonly open = signal(false);
  readonly active = signal(-1);
  readonly items = signal<Suggestion[]>([]);
  readonly note = signal("");

  private typing = new Subject<string>();
  private sub = new Subscription();

  get control(): FormControl {
    return this.form().get(this.field()) as FormControl;
  }

  ngOnInit(): void {
    this.sub.add(
      this.typing
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          switchMap((text) => {
            if (text.trim().length < 3) return of(null);
            this.note.set("");
            return from(this.search(text)).pipe(
              catchError(() => {
                this.note.set("Servizio indirizzi non raggiungibile. Compila l'indirizzo a mano.");
                return of([] as Suggestion[]);
              }),
            );
          }),
        )
        .subscribe((found) => {
          if (found === null) return this.close();
          this.items.set(found);
          if (!found.length && !this.note()) this.note.set("Nessun suggerimento: compila l'indirizzo a mano.");
          this.open.set(true);
          this.active.set(-1);
        }),
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  onInput(): void {
    this.form().patchValue({ lat: "", lng: "" }, { emitEvent: false });
    this.typing.next(String(this.control.value ?? ""));
  }

  onKey(event: KeyboardEvent): void {
    if (!this.open()) return;
    if (event.key === "Escape") return this.close();
    const n = this.items().length;
    if (!n) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.active.set((this.active() + 1) % n);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      this.active.set((this.active() - 1 + n) % n);
    } else if (event.key === "Enter" && this.active() > -1) {
      event.preventDefault();
      this.choose(this.items()[this.active()]);
    }
  }

  choose(item: Suggestion): void {
    this.close();

    const patch: { [name: string]: any } = { [this.field()]: item.address, lat: item.lat.toFixed(7), lng: item.lng.toFixed(7) };
    ["zip", "city", "province", "region", "country"].forEach((name) => {
      if (this.form().get(name)) patch[name] = (item as any)[name] || "";
    });
    this.form().patchValue(patch);
    this.form().markAsDirty();

    const at = patch["lat"] + "|" + patch["lng"];
    this.placeDetails(item.lat, item.lng).then((d) => {
      const form = this.form();
      if (form.get("lat")?.value + "|" + form.get("lng")?.value !== at) return;
      const more: { [name: string]: string } = {};
      if (d.province && form.get("province")) more["province"] = d.province;
      const region = d.region || (d.cityState ? item.city : "");
      if (region && form.get("region") && !form.get("region")?.value) more["region"] = region;
      if (Object.keys(more).length) form.patchValue(more);
    });
  }

  close(): void {
    this.open.set(false);
    this.active.set(-1);
  }

  onBlur(): void {
    setTimeout(() => this.close(), 150);
  }

  private async search(text: string): Promise<Suggestion[]> {
    const context = ["city", "country"].map((name) => String(this.form().get(name)?.value ?? "").trim()).filter(Boolean);
    const narrowed = context.length ? [text.trim(), ...context].join(", ") : null;
    const [near, anywhere] = await Promise.all([
      narrowed ? this.lookup(narrowed, text).catch(() => [] as Suggestion[]) : Promise.resolve([] as Suggestion[]),
      this.lookup(text.trim(), text),
    ]);
    const seen = new Set<string>();
    return [...near, ...anywhere]
      .filter((item) => {
        const key = item.address + "|" + item.detail;
        return seen.has(key) ? false : (seen.add(key), true);
      })
      .map((item, index) => ({ item, index }))
      .sort((x, y) => y.item.score - x.item.score || x.index - y.index)
      .map((x) => x.item)
      .slice(0, 5);
  }

  private async lookup(q: string, typed: string): Promise<Suggestion[]> {
    const response = await fetch(`${this.photonUrl()}/api/?limit=8&lang=default&q=${encodeURIComponent(q)}`, {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error("suggest");
    const data = await response.json();

    const out: Suggestion[] = [];
    for (const feature of data.features ?? []) {
      const p = feature.properties ?? {};
      if (p.type !== "house" && p.type !== "street") continue;
      const countries = this.countries();
      if (countries.length && !countries.includes(String(p.countrycode ?? "").toLowerCase())) continue;
      const street = p.street || p.name || "";
      const score = street
        ? this.score(typed, street, [p.city, p.town, p.village, p.district, p.county, p.state, p.country].join(" "))
        : -1;
      if (score < 0) continue;

      const item: Suggestion = {
        score,
        address: street + (p.housenumber ? " " + p.housenumber : ""),
        zip: p.postcode || "",
        city: p.city || p.town || p.village || p.district || "",
        province: p.county || "",
        region: p.state || "",
        country: p.country || "",
        lat: feature.geometry.coordinates[1],
        lng: feature.geometry.coordinates[0],
        detail: "",
      };
      item.detail = [[item.zip, item.city].filter(Boolean).join(" "), item.region, item.country].filter(Boolean).join(", ");
      out.push(item);
    }
    return out;
  }

  private forms(text: string): string[] {
    const t = String(text ?? "")
      .toLowerCase()
      .replace(/ß/g, "ss").replace(/æ/g, "ae").replace(/œ/g, "oe").replace(/ø/g, "o").replace(/ł/g, "l").replace(/đ/g, "d");
    const de = t.replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue");
    const strip = (x: string) => x.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    return [strip(de), strip(t)];
  }

  private score(typed: string, street: string, place: string): number {
    const tokens: string[] = [];
    this.forms(typed).forEach((f) => f.split(/[^a-z]+/).forEach((w) => w.length >= 4 && !tokens.includes(w) && tokens.push(w)));
    if (!tokens.length) return 0;
    const words = (text: string) => this.forms(text).map((f) => f.split(/[^a-z0-9]+/).filter(Boolean));
    const inStreet = words(street), inPlace = words(place);
    let total = 0, streetHit = false;
    for (const tok of tokens) {
      const starts = (lists: string[][]) => lists.some((ws) => ws.some((w) => w.startsWith(tok)));
      if (starts(inStreet)) { total += tok.length * 2; streetHit = true; }
      else if (starts(inPlace)) { total += tok.length; }
    }
    return streetHit ? total : -1;
  }

  private async placeDetails(lat: number, lng: number): Promise<{ province: string; region: string; cityState: boolean }> {
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&zoom=10&addressdetails=1&lat=${lat}&lon=${lng}`,
        { headers: { Accept: "application/json" } },
      );
      if (!r.ok) return { province: "", region: "", cityState: false };
      const a = (await r.json())?.address ?? {};
      const iso: string = a["ISO3166-2-lvl6"] ?? "";
      return {
        province: iso.includes("-") ? iso.split("-")[1] : "",
        region: a.state || a.region || "",
        cityState: !(a.state || a.region) && !!a["ISO3166-2-lvl4"],
      };
    } catch {
      return { province: "", region: "", cityState: false };
    }
  }
}
